import { useAdjustPoints, useCustomerLoyalty } from '@/features/loyalty/hooks/useLoyalty';
import { Block, BlockTitle, Button, List, ListInput, ListItem, f7 } from 'framework7-react';
import { useState } from 'react';

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

const ENTRY_LABEL: Record<string, string> = {
  EARN: 'Earned',
  REDEEM: 'Redeemed',
  ADJUST: 'Adjusted',
};

/** Points balance + recent ledger + owner manual adjust, for the customer profile. */
export function LoyaltyProfileSection({ customerId }: { customerId: string }) {
  const { data: loyalty } = useCustomerLoyalty(customerId);
  const adjust = useAdjustPoints(customerId);
  const [open, setOpen] = useState(false);
  const [points, setPoints] = useState('');
  const [reason, setReason] = useState('');

  if (!loyalty) return null;

  const apply = () => {
    const n = parseInt(points, 10);
    if (!Number.isInteger(n) || n === 0) {
      f7.toast.show({
        text: 'Enter a non-zero point amount.',
        closeTimeout: 2500,
        position: 'bottom',
      });
      return;
    }
    if (reason.trim().length === 0) {
      f7.toast.show({ text: 'A reason is required.', closeTimeout: 2500, position: 'bottom' });
      return;
    }
    adjust.mutate(
      { points: n, reason: reason.trim() },
      {
        onSuccess: () => {
          f7.toast.show({ text: 'Points adjusted', closeTimeout: 2000, position: 'bottom' });
          setOpen(false);
          setPoints('');
          setReason('');
        },
      },
    );
  };

  return (
    <>
      <BlockTitle>Loyalty</BlockTitle>
      <List strongIos strongMd dividersIos dividersMd insetIos insetMd>
        <ListItem title="Points balance" after={`${loyalty.balancePoints}`} />
        {loyalty.entries.slice(0, 5).map((e) => (
          <ListItem
            key={e.id}
            title={`${ENTRY_LABEL[e.entryType] ?? e.entryType}${e.reason ? ` · ${e.reason}` : ''}`}
            footer={fmtDate(e.createdAt)}
            after={`${e.points > 0 ? '+' : ''}${e.points}`}
          />
        ))}
      </List>

      {open ? (
        <List strongIos strongMd dividersIos dividersMd insetIos insetMd>
          <ListInput
            label="Adjustment (points, + or −)"
            type="number"
            placeholder="e.g. 50 or -20"
            value={points}
            onInput={(e) => setPoints(e.target.value)}
          />
          <ListInput
            label="Reason"
            type="text"
            placeholder="Goodwill / correction"
            value={reason}
            onInput={(e) => setReason(e.target.value)}
          />
          <ListItem>
            <Button slot="title" fill onClick={apply} disabled={adjust.isPending}>
              {adjust.isPending ? 'Applying…' : 'Apply adjustment'}
            </Button>
          </ListItem>
        </List>
      ) : (
        <Block>
          <Button outline onClick={() => setOpen(true)}>
            Adjust points
          </Button>
        </Block>
      )}
    </>
  );
}
